import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import pluralize from 'pluralize';
import { useMediaQuery } from 'react-responsive';
import lodashUniq from 'lodash/fp/uniq';

import { MEDIA_QUERY_DESKTOP_SCREEN } from 'client/constants/mediaQueries';

import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrAggressiveBreedsSelect from 'client/components/SrAggressiveBreedsSelect/SrAggressiveBreedsSelect';
import SrModalV2, {
  MODAL_THEME_FIXED_FOOTER_BUTTON,
} from 'client/components/SrModalV2/SrModalV2';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import { createFormFieldsByIdGetter } from 'client/helpers/formikHelpers';

import { AGGRESSIVE_BREEDS_LIST } from 'common/breedRestrictionsConstants';

const getFormFields = ({
  values,
  setFieldValue,
  setSelectAggressiveBreeds,
  breedRestrictionDefinitions,
}) => {
  return [
    {
      id: 'all',
      type: 'multiple_choice',
      properties: {
        multipleSelection: false,
        useArrayValues: false,
        toggleSingleSelection: true,
        checkboxIconType: 'cancelIconRed',
        choices: [{
          label: values.all
            ? 'Selected all usually restricted breeds'
            : 'Select all usually restricted breeds',
          value: true,
        }],
        onChange: (newValue) => {
          setFieldValue('all', newValue);
          if (newValue === true) {
            setFieldValue('breedRestrictions', breedRestrictionDefinitions);
          }
          else {
            setFieldValue('breedRestrictions', []);
          }
        },
      },
    },
    {
      id: 'breedRestrictions',
      type: 'multiple_choice',
      properties: {
        multipleSelection: true,
        useArrayValues: true,
        checkboxIconType: 'cancelIconRed',
        choices: breedRestrictionDefinitions.map((breedName) => ({
          label: breedName,
          value: breedName,
        })),
        autoFitWidth: '255px',
        onChange: (newValue) => {
          const selectedAllAggressiveBreeds = _selectedAllAggressiveBreeds(newValue);
          setSelectAggressiveBreeds(selectedAllAggressiveBreeds);

          setFieldValue('breedRestrictions', newValue);
          setFieldValue('all', newValue.length === breedRestrictionDefinitions.length);
        },
      },
    },
  ];
};

const _selectedAllAggressiveBreeds = (selectedBreeds) => {
  return AGGRESSIVE_BREEDS_LIST.every((breed) => selectedBreeds.includes(breed));
};

const SrBreedRestrictionsModal = ({
  breedRestrictions,
  breedRestrictionDefinitions,
  onClose,
  onSave,
}) => {
  const isDesktopScreen = useMediaQuery({
    query: MEDIA_QUERY_DESKTOP_SCREEN,
  });

  const selectedAllAggressiveBreeds = _selectedAllAggressiveBreeds(breedRestrictions);
  const [selectAggressiveBreeds, setSelectAggressiveBreeds] = useState(selectedAllAggressiveBreeds);

  return (
    <Formik
      initialValues={{
        breedRestrictions: breedRestrictions,
        all: breedRestrictions.length === breedRestrictionDefinitions.length,
      }}
    >
      {(formikBag) => {
        const { values, setFieldValue } = formikBag;

        const handleSelectAggressiveBreeds = () => {
          const uniqueBreeds = lodashUniq([...values.breedRestrictions, ...AGGRESSIVE_BREEDS_LIST]);

          if (!selectAggressiveBreeds) {
            setFieldValue('breedRestrictions', uniqueBreeds);
            setFieldValue('all', uniqueBreeds.length === breedRestrictionDefinitions.length);
          }

          const selectedAllAggressiveBreeds = _selectedAllAggressiveBreeds(values.breedRestrictions);
          setSelectAggressiveBreeds(selectedAllAggressiveBreeds || !selectAggressiveBreeds);
        };

        const formFields = getFormFields({
          values,
          breedRestrictionDefinitions,
          setFieldValue,
          setSelectAggressiveBreeds,
        });

        const getFormFieldsById = createFormFieldsByIdGetter(formFields);

        return (
          <SrModalV2
            theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
            buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
            onClose={onClose}
            contentPadding='28px 0 50px 0'
            buttonProps={[
              {
                text: 'Close',
                theme: SR_BUTTON_THEME_ADOBE_GREY,
                onClick: onClose,
              },
              {
                text: 'Save',
                theme: SR_BUTTON_THEME_ADOBE_GREEN,
                onClick: () => onSave(values.breedRestrictions),
              },
            ]}
          >
            <SrBox>
              <SrBox padding='0 10px 0 25px'>
                <SrText
                  font='greycliff'
                  paddingBottom='15px'
                  size='30'
                  weight='bolder'
                  display='block'
                >
                  Breed Restrictions
                </SrText>
                <SrText
                  font='greycliff'
                  paddingBottom='17px'
                  weight='bold'
                  color='grey600'
                  display='block'
                >
                  Choose dog breeds that are not allowed.
                </SrText>
              </SrBox>
              <SrBox
                bgColor='yellow-lighter'
                padding='10px 25px'
              >
                <SrText
                  font='greycliff'
                  fontSize='16px'
                  weight='800'
                  display='block'
                >
                  {values.breedRestrictions.length > 0
                    ? `${values.breedRestrictions.length}
                    ${pluralize('breed', values.breedRestrictions.length)} selected`
                    : 'No breeds selected'}
                </SrText>
              </SrBox>
              <SrAggressiveBreedsSelect
                handleClick={handleSelectAggressiveBreeds}
                isActive={selectAggressiveBreeds}
              />
              <SrBox
                maxHeight={isDesktopScreen ? 'calc(80vh - 210px)' : 'calc(100vh - 300px)'}
                overflow='auto'
                padding={!isDesktopScreen ? '0 15px 100px 20px' : '0 15px 20px 20px'}
                boxSizing='border-box'
              >
                <Form className='form'>
                  <SrBox
                    bgColor='grey200'
                    margin='0 -30px'
                    padding='5px 30px'
                    display='none'
                  >
                    <SrFormikField
                      formField={getFormFieldsById('all')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrBox>
                  <SrFormikField
                    formField={getFormFieldsById('breedRestrictions')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                </Form>
              </SrBox>
            </SrBox>
          </SrModalV2>
        );
      }}
    </Formik>
  );
};

SrBreedRestrictionsModal.propTypes = {
  breedRestrictions: PropTypes.array,
  breedRestrictionDefinitions: PropTypes.array,
  onClose: PropTypes.func.isRequired,
};

SrBreedRestrictionsModal.displayName = 'SrBreedRestrictionsModal';

export default SrBreedRestrictionsModal;
